import React, { useEffect, useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { settingsApi } from '../../api/settingsApi';
import { legalApi } from '../../api/legalApi';
import { LegalDocsView } from '../components/LegalDocsView';
import { Icons } from '../design-system/icons';

const accentBtn: React.CSSProperties = {
  background: 'var(--rf-accent-grad, linear-gradient(135deg,#2e86ff,#7c5cff))',
  color: '#fff', border: 'none', padding: '15px 24px', borderRadius: 14,
  fontSize: 16, fontWeight: 600, width: '100%', cursor: 'pointer',
  boxShadow: 'var(--rf-glow, 0 6px 20px rgba(46,134,255,0.35))',
};
const inputStyle: React.CSSProperties = {
  padding: 15, borderRadius: 12, background: 'var(--rf-surface, rgba(255,255,255,0.06))',
  border: '1px solid var(--rf-border, rgba(255,255,255,0.14))', color: 'var(--rf-text, #e8ecf4)',
  fontSize: 16, outline: 'none', width: '100%', boxSizing: 'border-box',
};

type Phase = 'consent' | 'welcome' | 'connect';

// onDone вызывается только когда пользователь реально прошёл все шаги:
// раньше родитель снимал онбординг сразу после принятия ToS.
export default function Onboarding({ onDone }: { onDone?: () => void }) {
  const queryClient = useQueryClient();

  const { data: legal } = useQuery({ queryKey: ['legal'], queryFn: () => legalApi.getStatus() });
  const { data: providers } = useQuery({ queryKey: ['providers'], queryFn: () => settingsApi.listProviders() });

  const needsConsent = legal?.needsConsent !== false; // пока грузится — считаем, что нужно
  const hasProvider = !!providers?.env_provider || !!providers?.providers?.some((p) => p.has_key);

  // ToS — первый экран. Пока состояние согласия неизвестно, phase = null,
  // чтобы приветствие не мелькало перед экраном документов.
  const [phase, setPhase] = useState<Phase | null>(null);
  const [agree, setAgree] = useState(false);
  const [key, setKey] = useState('');

  useEffect(() => {
    if (phase !== null || !legal) return;
    setPhase(legal.needsConsent ? 'consent' : hasProvider ? 'welcome' : 'connect');
  }, [legal, phase]);

  const finish = () => {
    queryClient.invalidateQueries({ queryKey: ['providers'] });
    queryClient.invalidateQueries({ queryKey: ['legal'] });
    queryClient.invalidateQueries({ queryKey: ['account'] });
    // Только здесь онбординг считается пройденным — после этого родитель разрешает тур.
    onDone?.();
  };

  const acceptMutation = useMutation({
    mutationFn: () => legalApi.accept(),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['legal'] });
      // With a platform/user provider, required consent completes the gate immediately.
      if (hasProvider) finish();
      else setPhase('connect');
    },
  });

  const keyMutation = useMutation({
    mutationFn: async (apiKey: string) => {
      await settingsApi.upsertProvider({
        id: 'openai', label: 'OpenAI', type: 'openai',
        models: { brain: 'gpt-4o', mid: 'gpt-4o-mini', cheap: 'gpt-4o-mini' },
        key: apiKey,
      });
      await settingsApi.setDefaultProvider('openai');
    },
    onSuccess: finish,
  });

  const startFlow = () => {
    if (needsConsent) setPhase('consent');
    else if (!hasProvider) setPhase('connect');
    else finish();
  };

  // Пока не знаем, нужно ли согласие — ничего не показываем.
  if (phase === null) {
    return (
      <div className="rf-detail">
        <div className="rf-detail-scroll" style={{ padding: 24, textAlign: 'center' }}>Загрузка…</div>
      </div>
    );
  }

  // ── Шаг 1: согласие с документами (ToS до всего остального) ────
  if (phase === 'consent') {
    return (
      <div className="rf-detail">
        <div className="rf-detail-scroll" style={{ padding: '20px 18px 40px' }}>
          <h1 style={{ fontSize: 23, margin: '4px 0 10px' }}>Немного формальностей</h1>
          <p style={{ color: 'var(--rf-text-2, #96a0b5)', lineHeight: 1.5, fontSize: 14.5, marginBottom: 16 }}>
            Платформа работает в режиме бесплатного пробного периода и использует сторонние ИИ-модели.
            Ответы ИИ могут содержать ошибки. Ознакомьтесь с документами ниже и подтвердите согласие.
          </p>

          {legal?.documents && <LegalDocsView docs={legal.documents} bounded />}

          <label style={{ display: 'flex', alignItems: 'flex-start', gap: 10, cursor: 'pointer', fontSize: 14, margin: '18px 0', lineHeight: 1.4 }}>
            <input type="checkbox" checked={agree} onChange={(e) => setAgree(e.target.checked)} style={{ marginTop: 3, width: 18, height: 18, flexShrink: 0 }} />
            <span>Я прочитал(а) и принимаю Условия использования и Политику конфиденциальности.</span>
          </label>

          {acceptMutation.isError && <div style={{ color: 'var(--rf-danger, #ff6b6b)', fontSize: 14, marginBottom: 12 }}>Не удалось сохранить согласие. Повторите.</div>}

          <button
            onClick={() => acceptMutation.mutate()}
            disabled={!agree || acceptMutation.isPending}
            style={{ ...accentBtn, opacity: (!agree || acceptMutation.isPending) ? 0.5 : 1, cursor: (!agree || acceptMutation.isPending) ? 'not-allowed' : 'pointer' }}
          >
            {acceptMutation.isPending ? 'Сохранение…' : 'Принимаю и продолжаю'}
          </button>
        </div>
      </div>
    );
  }

  // ── Шаг 2: приветствие и рассказ о платформе ────────────
  if (phase === 'welcome') {
    return (
      <div className="rf-detail">
        <div className="rf-detail-scroll" style={{ display: 'flex', flexDirection: 'column', justifyContent: 'center', minHeight: '80vh', textAlign: 'center', padding: '24px 20px' }}>
          <div style={{ margin: '0 auto 24px' }}>
            <Icons.Assistant size={64} color="var(--rf-accent, #2e86ff)" />
          </div>
          <h1 style={{ fontSize: 27, marginBottom: 14 }}>Добро пожаловать</h1>
          <p style={{ color: 'var(--rf-text-2, #96a0b5)', marginBottom: 30, lineHeight: 1.5 }}>
            Это не просто чат. Это продвинутый конструктор автоматизаций, где Ассистент
            может делегировать задачи агентам и сервисам вне Telegram.
          </p>
          <button onClick={startFlow} style={accentBtn}>Начать</button>
        </div>
      </div>
    );
  }

  // ── Шаг 3: подключение AI ─────────────────────────
  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    if (key.trim()) keyMutation.mutate(key.trim());
  };

  return (
    <div className="rf-detail">
      <div className="rf-detail-scroll" style={{ display: 'flex', flexDirection: 'column', justifyContent: 'center', minHeight: '80vh', padding: '24px 20px' }}>
        <h1 style={{ fontSize: 23, marginBottom: 10, textAlign: 'center' }}>Подключение AI</h1>
        {hasProvider ? (
          <>
            <p style={{ color: 'var(--rf-text-2, #96a0b5)', lineHeight: 1.5, fontSize: 14.5, marginBottom: 24, textAlign: 'center' }}>
              Модель уже доступна на ключах платформы — можно начинать. При желании позже
              подключите свой ключ (BYOK) в настройках.
            </p>
            <button onClick={finish} style={accentBtn}>Начать пользоваться</button>
          </>
        ) : (
          <>
            <p style={{ color: 'var(--rf-text-2, #96a0b5)', lineHeight: 1.5, fontSize: 14.5, marginBottom: 20, textAlign: 'center' }}>
              Платформа может работать на вашем ключе (BYOK). Введите ваш API-ключ OpenAI.
              Ключ хранится в зашифрованном виде и используется только для ваших автоматизаций.
            </p>
            <form onSubmit={handleSubmit} style={{ display: 'flex', flexDirection: 'column', gap: 14 }}>
              <input type="password" placeholder="sk-..." value={key} onChange={(e) => setKey(e.target.value)} style={inputStyle} />
              {keyMutation.isError && <div style={{ color: 'var(--rf-danger, #ff6b6b)', fontSize: 14 }}>Ошибка проверки ключа.</div>}
              <button
                disabled={keyMutation.isPending || !key.trim()}
                type="submit"
                style={{ ...accentBtn, opacity: (keyMutation.isPending || !key.trim()) ? 0.5 : 1 }}
              >
                {keyMutation.isPending ? 'Проверка…' : 'Сохранить и продолжить'}
              </button>
            </form>
          </>
        )}
      </div>
    </div>
  );
}
